import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Leaf, Sparkles, HandHeart, Trees, BookOpen, Pencil } from "lucide-react";
import { api } from "@/lib/api";
import { ArticleCard } from "@/pages/Articles";
import NewsletterForm from "@/components/NewsletterForm";

const HERO_IMG = "/hero.jpg";
const CRAFT_IMG =
  "https://images.unsplash.com/photo-1588007111491-6047209f60bd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxmZWx0JTIwY3JhZnRzJTIwd2FsZG9yZnxlbnwwfHx8fDE3ODI5MDgzODZ8MA&ixlib=rb-4.1.0&q=85";
const NATURE_IMG =
  "https://images.unsplash.com/photo-1768509197111-1deec3dd559a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwxfHxjaGlsZHJlbiUyMG5hdHVyZSUyMGVkdWNhdGlvbnxlbnwwfHx8fDE3ODI5MDgzODZ8MA&ixlib=rb-4.1.0&q=85";

const VALUE_ICONS = [HandHeart, Leaf, Sparkles];
const VALUE_KEYS = ["value_1", "value_2", "value_3"];
const FEATURE_KEYS = ["feature_forum", "feature_events", "feature_profile"];
const FEATURE_TO = {
  feature_forum: "/forum",
  feature_events: "/events",
  feature_profile: null, // resolved at render based on auth
};

export default function Landing() {
  const { user } = useAuth();
  const [latest, setLatest] = useState([]);
  const [content, setContent] = useState({});

  useEffect(() => {
    api.get("/articles", { params: { limit: 3 } }).then(({ data }) => setLatest(data));
    api.get("/content").then(({ data }) => setContent(data));
  }, []);

  const hero = content.hero;

  return (
    <div className="pb-24">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="k-container grid gap-12 pt-16 md:pt-24 lg:grid-cols-12 lg:gap-10">
          <div className="lg:col-span-7 animate-fade-up">
            {user?.role === "admin" && (
              <Link
                to="/admin/anasayfa"
                className="mb-4 inline-flex items-center gap-1.5 rounded-full border border-sage-500/30 bg-cream-50/70 px-3 py-1 text-xs font-semibold text-sage-700 hover:bg-cream-50"
                data-testid="edit-hero-btn"
              >
                <Pencil className="h-3.5 w-3.5" /> Açılışı düzenle
              </Link>
            )}
            <span className="k-tag mb-6 flex w-fit" data-testid="hero-tag">
              <Trees className="mr-2 h-3.5 w-3.5" /> Eğitmen ve Ebeveyn Topluluğu
            </span>
            <h1
              className="font-display text-6xl leading-[0.95] text-ink-900 md:text-7xl lg:text-8xl"
              data-testid="hero-headline"
            >
              {hero
                ? hero.title.split("\n").map((line, i, arr) => (
                    <span key={i}>
                      {line}
                      {i < arr.length - 1 && <br />}
                    </span>
                  ))
                : "Bu yolda"}
            </h1>
            {hero ? (
              <div
                className="k-prose mt-6 max-w-xl !text-lg text-ink-700"
                data-testid="hero-sub"
                dangerouslySetInnerHTML={{ __html: hero.body }}
              />
            ) : (
              <p className="mt-6 max-w-xl font-body text-lg leading-relaxed text-ink-700" data-testid="hero-sub-fallback">
                Yükleniyor…
              </p>
            )}

            <div className="mt-10 flex flex-wrap gap-4">
              {user ? (
                <>
                  <Link to="/forum" className="k-btn-primary" data-testid="hero-cta-forum">
                    Tartışmalara katıl
                  </Link>
                  <Link to="/events" className="k-btn-secondary" data-testid="hero-cta-events">
                    Etkinliklere göz at
                  </Link>
                </>
              ) : (
                <>
                  <Link to="/register" className="k-btn-primary" data-testid="hero-cta-join">
                    Katıl
                  </Link>
                  <Link to="/login" className="k-btn-secondary" data-testid="hero-cta-login">
                    Giriş yap
                  </Link>
                </>
              )}
            </div>

          </div>

          <div className="relative lg:col-span-5">
            <div className="relative">
              <div className="absolute -left-6 -top-6 h-40 w-40 rounded-full bg-sage-500/20 blur-3xl" />
              <div className="absolute -right-8 -bottom-8 h-56 w-56 rounded-full bg-brick-500/15 blur-3xl" />
              <img
                src={HERO_IMG}
                alt="Ahşap Waldorf oyuncakları"
                className="relative w-full rounded-[2.5rem_1.25rem_2.5rem_1.25rem] object-cover shadow-warm"
                style={{ aspectRatio: "4/5" }}
                data-testid="hero-image"
              />
              <img
                src={CRAFT_IMG}
                alt="Keçe el işleri"
                className="absolute -bottom-10 -left-10 hidden w-40 rounded-[1.5rem_1rem_1.5rem_1rem] border-4 border-cream-100 object-cover shadow-warm md:block lg:w-48"
                style={{ aspectRatio: "1/1" }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="k-container mt-24 md:mt-32">
        <div className="grid gap-6 md:grid-cols-3">
          {VALUE_KEYS.map((k, i) => {
            const v = content[k];
            const Icon = VALUE_ICONS[i];
            return (
              <div
                key={k}
                className="k-card p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-warm"
                style={{ animationDelay: `${i * 120}ms` }}
                data-testid={`value-card-${i}`}
              >
                <span className="grid h-12 w-12 place-items-center rounded-2xl bg-sage-500/15 text-sage-700">
                  <Icon className="h-6 w-6" />
                </span>
                <h3 className="mt-5 font-heading text-2xl font-bold text-ink-900">
                  {v?.title || "…"}
                </h3>
                <div
                  className="mt-3 font-body leading-relaxed text-ink-700"
                  dangerouslySetInnerHTML={{ __html: v?.body || "" }}
                />
              </div>
            );
          })}
        </div>
      </section>

      {/* What you'll find */}
      <section className="k-container mt-24 grid gap-12 lg:grid-cols-12 lg:gap-16">
        <div className="lg:col-span-5">
          <img
            src={NATURE_IMG}
            alt="Doğada çocuklar"
            className="w-full rounded-[2rem_1rem_2rem_1rem] object-cover shadow-soft"
            style={{ aspectRatio: "5/6" }}
            data-testid="nature-image"
          />
        </div>
        <div className="lg:col-span-7">
          <h2 className="font-display text-5xl text-ink-900 md:text-6xl">
            Kabile'de neler var?
          </h2>
          <p className="mt-4 font-body text-lg text-ink-700 max-w-xl">
            İlk aşamada üç şey: kendini samimi biçimde ifade edebileceğin bir sohbet alanı, birlikte
            büyüyeceğimiz atölyeler ve sadece bir kez ayarlayacağın bir profil.
          </p>

          <div className="mt-8 space-y-4">
            {FEATURE_KEYS.map((k) => {
              const f = content[k];
              if (!f) return null;
              const to = k === "feature_profile" ? (user ? "/profile" : "/register") : FEATURE_TO[k];
              return (
                <FeatureRow
                  key={k}
                  title={f.title}
                  htmlDesc={f.body}
                  to={to}
                  cta={f.cta_text || "Devamı"}
                  testid={k.replace("_", "-")}
                  editPath={user?.role === "admin" ? `/admin/icerik/${k}` : null}
                />
              );
            })}
          </div>
        </div>
      </section>

      {/* Latest articles */}
      {latest.length > 0 && (
        <section className="k-container mt-24" data-testid="latest-articles-section">
          <div className="flex items-end justify-between gap-4">
            <div>
              <span className="k-tag mb-3 inline-flex">
                <BookOpen className="mr-2 h-3.5 w-3.5" /> Okumalar
              </span>
              <h2 className="font-display text-5xl text-ink-900 md:text-6xl">Son yazılar</h2>
            </div>
            <Link to="/yazilar" className="k-btn-ghost hidden text-sage-700 sm:inline-flex" data-testid="latest-articles-more">
              Tümünü gör →
            </Link>
          </div>
          <div className="mt-8 grid gap-6 md:grid-cols-3">
            {latest.slice(0, 3).map((a) => (
              <ArticleCard key={a.article_id} a={a} />
            ))}
          </div>
        </section>
      )}

      {/* Newsletter */}
      <section className="k-container mt-24">
        <NewsletterForm source="landing" />
      </section>

      {/* About teaser */}
      <section className="k-container mt-24">
        <div className="k-card grid gap-8 p-8 md:grid-cols-5 md:p-12">
          <div className="md:col-span-3">
            <span className="k-tag" data-testid="about-teaser-tag">
              <Leaf className="mr-2 h-3.5 w-3.5" /> Hakkımızda
            </span>
            <h2 className="mt-4 font-display text-5xl text-ink-900 md:text-6xl" data-testid="about-teaser-title">
              Bir çocuğu bir köy büyütür.
            </h2>
            <p className="mt-4 font-body text-lg leading-relaxed text-ink-700">
              Biz Kübra ve Şevval. İki okul öncesi öğretmeni olarak çıktığımız bu yolda,
              deneyimlerimizi paylaştığımız ve birbirimizden öğrendiğimiz sıcak bir köy kurmak istedik.
            </p>
            <div className="mt-6">
              <Link to="/hakkimizda" className="k-btn-secondary" data-testid="about-teaser-cta">
                Hikâyeyi oku →
              </Link>
            </div>
          </div>
          <div className="md:col-span-2 grid place-items-center">
            <div className="rounded-[2rem_1rem_2rem_1rem] bg-earth-400/15 p-8 text-center">
              <p className="font-display text-4xl text-ink-900 leading-tight">
                Kabile'ne hoş geldin.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      {!user && (
        <section className="k-container mt-24">
          <div className="k-card overflow-hidden p-10 md:p-16 text-center">
            <h2 className="font-display text-5xl text-ink-900 md:text-6xl" data-testid="bottom-cta-title">
              Sen de kabileye katıl.
            </h2>
            <div className="mt-8 flex justify-center gap-4">
              <Link to="/register" className="k-btn-primary" data-testid="bottom-cta-register">
                Ücretsiz katıl
              </Link>
              <Link to="/login" className="k-btn-secondary" data-testid="bottom-cta-login">
                Zaten üyeyim
              </Link>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

function FeatureRow({ title, desc, htmlDesc, to, cta, testid, editPath }) {
  return (
    <div
      className="flex flex-col gap-3 rounded-2xl border border-cream-300 bg-cream-50 p-6 md:flex-row md:items-center md:justify-between"
      data-testid={testid}
    >
      <div>
        <div className="flex items-center gap-2">
          <h3 className="font-heading text-xl font-bold text-ink-900">{title}</h3>
          {editPath && (
            <Link
              to={editPath}
              className="inline-flex items-center gap-1 rounded-full border border-sage-500/30 bg-cream-100 px-2 py-0.5 text-xs font-semibold text-sage-700 transition-colors hover:bg-cream-50"
              data-testid={`edit-${testid}-btn`}
            >
              <Pencil className="h-3 w-3" /> Düzenle
            </Link>
          )}
        </div>
        {htmlDesc ? (
          <div className="mt-1 font-body text-ink-700" dangerouslySetInnerHTML={{ __html: htmlDesc }} />
        ) : (
          <p className="mt-1 font-body text-ink-700">{desc}</p>
        )}
      </div>
      <Link to={to} className="k-btn-ghost whitespace-nowrap text-sage-700" data-testid={`${testid}-cta`}>
        {cta} →
      </Link>
    </div>
  );
}
